import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import {
    Button,
    Spinner,
    Modal,
    Form,
    OverlayTrigger,
    Tooltip,
    Row,
    Col,
    Tab,
    Tabs,
    Card,
    Badge,
    Dropdown
} from "react-bootstrap";
import {
    FiEdit2,
    FiTrash2,
    FiDownload,
    FiFileText,
    FiFile,
    FiRefreshCw,
    FiPlus,
    FiLink,
    FiUpload,
    FiSearch,
    FiX,
    FiChevronDown,
    FiTrendingUp,
    FiAlertTriangle,
    FiPackage,
    FiDollarSign,
    FiCheckCircle,
    FiAlertCircle,
    FiImage,
    FiHash,
    FiGrid,
    FiMaximize2,
    FiMonitor,
    FiAlignLeft
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

const initialForm = {
    sku: "",
    product_name: "",
    category: "",
    color: "",
    capacity: "",
    size_and_weight: "",
    display: "",
    product_description: "",
    price: "",
    sales_price: "",
    discount_price: "",
    margin_price: "",
    quantity: "",
    status: "Active",
    product_img: ""
};

const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" }
];

const Products = () => {
    const [products, setProducts] = useState([]);
    const [usedProductIds, setUsedProductIds] = useState([]);
    const [pending, setPending] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState("upload");
    const [userRole, setUserRole] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState(null);
    const navigate = useNavigate();
    const fetchingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [statsCollapsed, setStatsCollapsed] = useState(true);

    useEffect(() => {
        const role = localStorage.getItem("user_role");
        setUserRole(role || "");
        fetchData();
    }, []);

    const hasEditPermission = ["admin", "supervisor", "editor", "superadmin"].includes(userRole);

    const fetchData = async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setPending(true);

            const [productsRes, usedProductIdsRes] = await Promise.all([
                axios.get("https://www.azzenterprises.com/api/products", {
                    withCredentials: true
                }),
                axios.get("https://www.azzenterprises.com/api/customers/product-ids")
            ]);

            setProducts(productsRes.data);

            const productIdsAsNumbers = usedProductIdsRes.data.productIds.map(id => parseInt(id));
            setUsedProductIds(productIdsAsNumbers);

        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            if (serverMsg === "Token required") {
                if (!toast.isActive("token-expired")) {
                    toast.error("Session expired. Redirecting to login...", {
                        toastId: "token-expired"
                    });
                }
                setTimeout(() => navigate("/login"), 1300);
            } else {
                console.error("Failed to fetch used product IDs:", err);
                try {
                    const productsRes = await axios.get("https://www.azzenterprises.com/api/products", {
                        withCredentials: true
                    });
                    setProducts(productsRes.data);
                    setUsedProductIds([]);
                } catch (productsErr) {
                    console.error("Failed to fetch products:", productsErr);
                }
            }
        } finally {
            setPending(false);
            fetchingRef.current = false;
        }
    };

    const productStats = useMemo(() => {
        const stats = {
            totalProducts: products.length,
            activeProducts: 0,
            inactiveProducts: 0,
            totalStockValue: 0,
            totalQuantity: 0,
            averagePrice: 0,
            categories: {},
            lowStockProducts: 0,
            criticalStockProducts: 0,
            outOfStockProducts: 0
        };

        let totalPrice = 0;

        products.forEach(product => {
            const price = parseFloat(product.price) || 0;
            const quantity = parseInt(product.quantity) || 0;
            const stockValue = price * quantity;

            if (product.status === "Active") {
                stats.activeProducts++;
            } else {
                stats.inactiveProducts++;
            }

            stats.totalStockValue += stockValue;
            stats.totalQuantity += quantity;
            totalPrice += price;

            const category = product.category || "Uncategorized";
            stats.categories[category] = (stats.categories[category] || 0) + 1;

            if (quantity === 0) {
                stats.outOfStockProducts++;
            } else if (quantity === 1) {
                stats.criticalStockProducts++;
            } else if (quantity < 3) {
                stats.lowStockProducts++;
            }
        });

        stats.averagePrice = products.length > 0 ? totalPrice / products.length : 0;

        const totalStockProducts = stats.lowStockProducts + stats.criticalStockProducts + stats.outOfStockProducts;
        stats.stockAlertProducts = totalStockProducts;
        stats.stockAlertPercentage = products.length > 0 ? ((totalStockProducts / products.length) * 100).toFixed(1) : 0;

        return stats;
    }, [products]);

    const categoryOptions = useMemo(() => {
        const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
        return [
            { value: "", label: "All Categories" },
            ...categories.map(category => ({
                value: category,
                label: category
            }))
        ];
    }, [products]);

    const formatPrice = (price) =>
        new Intl.NumberFormat("en-SG", {
            style: "currency",
            currency: "SGD"
        }).format(price);

    const filteredProducts = useMemo(() => {
        let filtered = products;

        if (activeFilter) {
            switch (activeFilter.type) {
                case 'status':
                    filtered = filtered.filter(product => product.status === activeFilter.value);
                    break;
                case 'category':
                    if (activeFilter.value) {
                        filtered = filtered.filter(product => product.category === activeFilter.value);
                    }
                    break;
                case 'stock':
                    if (activeFilter.value === 'critical') {
                        filtered = filtered.filter(product => {
                            const quantity = parseInt(product.quantity) || 0;
                            return quantity === 1;
                        });
                    } else if (activeFilter.value === 'low') {
                        filtered = filtered.filter(product => {
                            const quantity = parseInt(product.quantity) || 0;
                            return quantity === 2;
                        });
                    } else if (activeFilter.value === 'out') {
                        filtered = filtered.filter(product => {
                            const quantity = parseInt(product.quantity) || 0;
                            return quantity === 0;
                        });
                    }
                    break;
                default:
                    break;
            }
        }

        if (searchTerm) {
            filtered = filtered.filter(
                (p) =>
                    (p.product_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.sku || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.category || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.size_and_weight || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.display || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.product_description || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [products, searchTerm, activeFilter]);

    const isProductInUse = (productId) => {
        return usedProductIds.includes(parseInt(productId));
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirmed = async () => {
        try {
            await axios.delete(`https://www.azzenterprises.com/api/products/${deleteId}`, {
                withCredentials: true
            });
            fetchData();
            toast.success("Product deleted successfully");
        } catch {
            toast.error("Failed to delete product");
        } finally {
            setShowDeleteModal(false);
            setDeleteId(null);
        }
    };

    const handleExportExcel = () => {
        // Add the missing columns to the export data
        const exportData = filteredProducts.map(product => ({
            "ID": product.product_id,
            "SKU": product.sku || "-",
            "Product Name": product.product_name || "-",
            "Category": product.category || "-",
            "Color": product.color || "-",
            "Capacity": product.capacity || "-",
            "Size & Weight": product.size_and_weight || "-",
            "Display": product.display || "-",
            "Description": product.product_description || "-",
            "Price": formatPrice(product.price || 0),
            "Sale Price": formatPrice(product.sales_price || 0),
            "Discount Price": formatPrice(product.discount_price || 0),
            "Margin Price": formatPrice(product.margin_price || 0),
            "Quantity": product.quantity || "0",
            "Status": product.status || "-",
            "Image URL": product.product_img || "-"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, "products.xlsx");
        toast.success("Exported to Excel");
    };

    const handleExportCSV = () => {
        // Add the missing columns to the export data
        const exportData = filteredProducts.map(product => ({
            "ID": product.product_id,
            "SKU": product.sku || "-",
            "Product Name": product.product_name || "-",
            "Category": product.category || "-",
            "Color": product.color || "-",
            "Capacity": product.capacity || "-",
            "Size & Weight": product.size_and_weight || "-",
            "Display": product.display || "-",
            "Description": product.product_description || "-",
            "Price": formatPrice(product.price || 0),
            "Sale Price": formatPrice(product.sales_price || 0),
            "Discount Price": formatPrice(product.discount_price || 0),
            "Margin Price": formatPrice(product.margin_price || 0),
            "Quantity": product.quantity || "0",
            "Status": product.status || "-",
            "Image URL": product.product_img || "-"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, "products.csv");
        toast.success("Exported to CSV");
    };

    const handleExportPDF = () => {
        if (filteredProducts.length === 0) {
            toast.warn("No data to export");
            return;
        }

        try {
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: 'a4'
            });

            const margin = 2;
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;

            doc.setFontSize(14);
            doc.setTextColor(30, 41, 59);
            doc.text("Products Master List", margin, margin + 8);

            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text(`Total: ${filteredProducts.length} products | Generated: ${new Date().toLocaleDateString()}`, margin, margin + 14);

            // Updated PDF data with missing columns
            const pdfData = filteredProducts.map((p) => [
                p.product_id || "-",
                p.sku || "-",
                p.product_name || "-",
                p.category || "-",
                p.color || "-",
                p.capacity || "-",
                p.size_and_weight || "-",
                p.display || "-",
                (p.product_description || "").substring(0, 50) + ((p.product_description || "").length > 50 ? "..." : ""),
                formatPrice(p.price || 0),
                formatPrice(p.sales_price || 0),
                formatPrice(p.discount_price || 0),
                formatPrice(p.margin_price || 0),
                p.quantity || "0",
                p.status || "-"
            ]);

            // Updated headers with missing columns
            const headers = [
                "ID", "SKU", "Name", "Category", "Color", "Capacity",
                "Size/Weight", "Display", "Description", "Price", "Sale Price",
                "Discount", "Margin", "Qty", "Status"
            ];

            const columnStyles = {
                0: { cellWidth: 15 },
                1: { cellWidth: 20 },
                2: { cellWidth: 25 },
                3: { cellWidth: 20 },
                4: { cellWidth: 15 },
                5: { cellWidth: 15 },
                6: { cellWidth: 20 },
                7: { cellWidth: 20 },
                8: { cellWidth: 30 },
                9: { cellWidth: 15 },
                10: { cellWidth: 15 },
                11: { cellWidth: 15 },
                12: { cellWidth: 15 },
                13: { cellWidth: 10 },
                14: { cellWidth: 15 }
            };

            autoTable(doc, {
                head: [headers],
                body: pdfData,
                startY: margin + 18,
                styles: {
                    fontSize: 8,
                    cellPadding: 1,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.05,
                    textColor: [30, 30, 30],
                    minCellHeight: 3
                },
                headStyles: {
                    fillColor: [59, 130, 246],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 8,
                    halign: 'center',
                    cellPadding: 1
                },
                bodyStyles: {
                    fontSize: 7,
                    cellPadding: 1
                },
                columnStyles: columnStyles,
                margin: {
                    left: margin,
                    right: margin,
                    top: margin + 18,
                    bottom: margin + 5
                },
                tableWidth: pageWidth - (margin * 2),
                theme: 'grid',
                showHead: 'everyPage',
                horizontalPageBreak: false,
                didDrawPage: function (data) {
                    doc.setFontSize(6);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${data.pageNumber}`,
                        pageWidth - margin - 5,
                        pageHeight - margin
                    );
                },
                willDrawCell: function (data) {
                    if (data.cell.raw && data.cell.raw.toString().length > 25) {
                        data.cell.styles.fontSize = 6;
                    }
                }
            });

            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
            doc.save(`products_complete_${timestamp}.pdf`);

            toast.success(`PDF exported successfully`);

        } catch (error) {
            console.error("PDF export error:", error);
            toast.error("Failed to generate PDF");
        }
    };

    const openModal = (product = null) => {
        if (!hasEditPermission) return;
        if (product) {
            setEditingId(product.product_id);
            setFormData({ ...product });
            setImagePreview(product.product_img || "");
            setActiveTab(product.product_img?.startsWith("http") ? "url" : "upload");
        } else {
            setEditingId(null);
            setFormData(initialForm);
            setImagePreview("");
            setActiveTab("upload");
        }
        setImageFile(null);
        setShowModal(true);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error("File size exceeds 2MB limit");
            return;
        }

        const localPreview = URL.createObjectURL(file);
        setImageFile(file);
        setImagePreview(localPreview);
    };

    const handleImageUrlChange = (e) => {
        const url = e.target.value;
        setImagePreview(url);
        setFormData((prev) => ({ ...prev, product_img: url }));
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "number" ? Number(value) || 0 : value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.sku || !formData.product_name || !formData.sales_price || !formData.quantity) {
            toast.error("SKU, product name, sale price, and quantity are required");
            return;
        }

        try {
            setSubmitting(true);

            let imageUrl = formData.product_img;

            if (imageFile) {
                const formDataObj = new FormData();
                formDataObj.append("file", imageFile);

                const uploadRes = await axios.post(
                    "https://www.azzenterprises.com/api/aws/upload-product",
                    formDataObj,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        withCredentials: true,
                    }
                );

                imageUrl = uploadRes.data.url;
                toast.update("uploading", {
                    render: "Image uploaded successfully",
                    type: "success",
                    autoClose: 1200,
                });
            }

            const endpoint = editingId
                ? `https://www.azzenterprises.com/api/products/${editingId}`
                : `https://www.azzenterprises.com/api/products/addproduct`;
            const method = editingId ? "put" : "post";

            await axios[method](
                endpoint,
                { ...formData, product_img: imageUrl },
                { withCredentials: true }
            );

            toast.success(editingId ? "Product updated successfully" : "Product added successfully");
            setShowModal(false);
            fetchData();

            setFormData(initialForm);
            setImageFile(null);
            setImagePreview("");
            setEditingId(null);
        } catch (err) {
            console.error("Error saving product:", err);
            toast.error("Failed to save product");
        } finally {
            setSubmitting(false);
        }
    };

    const clearFilters = () => {
        setActiveFilter(null);
        setSearchTerm("");
    };

    const handleCategorySelect = (category) => {
        if (activeFilter && activeFilter.type === 'category' && activeFilter.value === category) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type: 'category', value: category });
        }
    };

    const handleStatusFilter = (status) => {
        if (activeFilter && activeFilter.type === 'status' && activeFilter.value === status) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type: 'status', value: status });
        }
    };

    const handleStockFilter = (type, value) => {
        if (activeFilter && activeFilter.type === type && activeFilter.value === value) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type, value });
        }
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            sortable: true,
            width: "60px"
        },
        {
            name: "ID",
            selector: (row) => row.product_id,
            sortable: true,
            width: "70px"
        },
        {
            name: "SKU",
            selector: (row) => row.sku || "-",
            width: "90px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiHash size={12} className="text-muted" />
                    <span className="font-semibold text-gray-600 small">
                        {row.sku || "-"}
                    </span>
                </div>
            )
        },
        {
            name: "Name",
            selector: (row) => row.product_name,
            sortable: true,
            width: "180px",
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: "170px" }} title={row.product_name}>
                    {row.product_name}
                </div>
            )
        },
        {
            name: "Category",
            selector: (row) => row.category || "-",
            width: "120px",
            cell: (row) => (
                <Badge bg="secondary" className="px-2 py-1">
                    {row.category || "-"}
                </Badge>
            )
        },
        {
            name: "Color",
            selector: (row) => row.color || "-",
            width: "100px"
        },
        {
            name: "Capacity",
            selector: (row) => row.capacity || "-",
            width: "100px"
        },
        {
            name: "Size / Weight",
            selector: (row) => row.size_and_weight || "-",
            width: "450px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiMaximize2 size={12} className="text-muted" />
                    <div
                        className="text-truncate small"
                        style={{ maxWidth: "430px" }}
                        title={row.size_and_weight}
                    >
                        {row.size_and_weight || "-"}
                    </div>
                </div>
            )
        },
        {
            name: "Display",
            selector: (row) => row.display || "-",
            width: "130px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiMonitor size={12} className="text-muted" />
                    <span className="small">
                        {row.display || "-"}
                    </span>
                </div>
            )
        },
        {
            name: "Description",
            selector: (row) => row.product_description || "-",
            width: "500px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiAlignLeft size={12} className="text-muted" />
                    <div className="text-truncate small" style={{ maxWidth: "480px" }} title={row.product_description}>
                        {row.product_description || "-"}
                    </div>
                </div>
            )
        },
        {
            name: "Price",
            selector: (row) => formatPrice(row.price || 0),
            sortable: true,
            width: "100px"
        },
        {
            name: "Sale Price",
            selector: (row) => formatPrice(row.sales_price || 0),
            width: "100px"
        },
        {
            name: "Qty",
            selector: (row) => row.quantity || 0,
            sortable: true,
            width: "80px",
            cell: (row) => {
                const quantity = parseInt(row.quantity) || 0;
                let color = "#16a34a";
                if (quantity === 0) color = "#6b7280";
                else if (quantity === 1) color = "#dc2626";
                else if (quantity < 3) color = "#f59e0b";

                return (
                    <div className="d-flex align-items-center gap-2">
                        <div style={{
                            width: "6px",
                            height: "6px",
                            backgroundColor: color,
                            borderRadius: "50%"
                        }} />
                        <span style={{ color, fontWeight: "600" }}>
                            {quantity}
                        </span>
                    </div>
                );
            }
        },
        {
            name: "Status",
            selector: (row) => row.status || "-",
            sortable: true,
            width: "100px",
            cell: (row) => {
                const isActive = row.status === "Active";
                return (
                    <Badge bg={isActive ? "success" : "secondary"} className="px-2 py-1">
                        {row.status || "-"}
                    </Badge>
                );
            }
        },
        {
            name: "Image",
            cell: (row) =>
                row.product_img ? (
                    <img
                        src={row.product_img}
                        alt="product"
                        width={40}
                        height={40}
                        style={{ objectFit: "cover", borderRadius: "6px" }}
                    />
                ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, backgroundColor: "#f3f4f6", borderRadius: "6px" }}>
                        <FiImage size={16} className="text-muted" />
                    </div>
                ),
            width: "80px"
        },
        ...(hasEditPermission
            ? [
                {
                    name: "Actions",
                    cell: (row) => {
                        const productInUse = isProductInUse(row.product_id);
                        return (
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => openModal(row)}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <FiEdit2 size={12} />
                                </Button>

                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            {productInUse
                                                ? "Product is in use by customers - cannot delete"
                                                : "Delete product"
                                            }
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        <Button
                                            variant={productInUse ? "outline-secondary" : "outline-danger"}
                                            size="sm"
                                            onClick={() => !productInUse && confirmDelete(row.product_id)}
                                            disabled={productInUse}
                                            className="d-flex align-items-center gap-1"
                                            style={{
                                                opacity: productInUse ? 0.5 : 1
                                            }}
                                        >
                                            <FiTrash2 size={12} />
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            </div>
                        );
                    },
                    width: "120px",
                    ignoreRowClick: true
                }
            ]
            : [])
    ];

    const StatCard = ({ title, value, subtitle, icon, color, filterType, filterValue, isActive, onFilter }) => {
        const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const handleClick = () => {
            if (onFilter) {
                onFilter();
            }
        };

        return (
            <Card
                className={`border-0 shadow-sm h-100 ${isActive ? 'active-stat-card' : ''}`}
                style={{
                    cursor: onFilter ? 'pointer' : 'default',
                    border: isActive ? `2px solid ${color}` : '1px solid #e5e7eb',
                    backgroundColor: isActive ? hexToRgba(color, 0.08) : '#ffffff',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    boxShadow: isActive ? `0 4px 12px ${hexToRgba(color, 0.15)}` : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                }}
                onClick={handleClick}
            >
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                                width: "48px",
                                height: "48px",
                                backgroundColor: isActive ? color : `${color}15`,
                                color: isActive ? '#fff' : color,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {React.cloneElement(icon, {
                                size: 20,
                                color: isActive ? '#fff' : color
                            })}
                        </div>
                        <div>
                            <h6 className="mb-1" style={{
                                fontSize: "0.875rem",
                                color: isActive ? color : '#6b7280',
                                fontWeight: isActive ? '600' : '400'
                            }}>
                                {title}
                            </h6>
                            <h4 className="fw-bold mb-0" style={{
                                color: isActive ? color : '#1f2937',
                                fontSize: "1.5rem"
                            }}>
                                {value}
                            </h4>
                            {subtitle && (
                                <small style={{
                                    fontSize: "0.75rem",
                                    color: isActive ? color : '#6b7280'
                                }}>
                                    {subtitle}
                                </small>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container-fluid py-2">
            <ToastContainer position="top-right" autoClose={1500} theme="light" />

            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <p className="fw-bold mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                                    Manage your product inventory and pricing
                                </p>
                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'status'
                                                    ? `Status: ${activeFilter.value}`
                                                    : activeFilter.type === 'category'
                                                        ? `Category: ${activeFilter.value || 'All'}`
                                                        : activeFilter.type === 'stock'
                                                            ? (activeFilter.value === 'critical' ? 'Critical Stock (1)' :
                                                                activeFilter.value === 'low' ? 'Low Stock (2)' :
                                                                    'Out of Stock')
                                                            : activeFilter.type}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={clearFilters}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                        style={{ backgroundColor: '#dcfce7' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#166534' }}>
                                            {productStats.activeProducts} Active
                                        </span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-secondary"
                                        style={{ backgroundColor: '#f3f4f6' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#6b7280' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#4b5563' }}>
                                            {productStats.inactiveProducts} Inactive
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FiDownload size={14} />
                                    <span className="d-none d-sm-inline">Export</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleExportCSV} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFileText size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">CSV Export</div>
                                                <small className="text-muted">Comma-separated values</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportExcel} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFile size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Excel Export</div>
                                                <small className="text-muted">Microsoft Excel format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportPDF} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiDownload size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">PDF Export</div>
                                                <small className="text-muted">Portable Document Format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-1 px-2 py-1 border rounded-2 shadow-sm text-nowrap"
                                    style={{ fontSize: '0.8rem', minWidth: '100px' }}
                                >
                                    <FiGrid size={12} />
                                    <span className="d-none d-md-inline">
                                        {activeFilter?.type === 'category' ? activeFilter.value || 'All' : 'Categories'}
                                    </span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="p-1 shadow-lg border-0 rounded-3" style={{ minWidth: '160px' }}>
                                    {categoryOptions.map((category) => (
                                        <Dropdown.Item
                                            key={category.value}
                                            onClick={() => handleCategorySelect(category.value)}
                                            active={activeFilter?.type === 'category' && activeFilter?.value === category.value}
                                            className="py-1 px-2 rounded-2 small cursor-pointer hover-bg-light transition-all"
                                            style={{ fontSize: '0.8rem', lineHeight: '25px' }}
                                        >
                                            <div className="d-flex align-items-center gap-2 w-100">
                                                <div className="text-start flex-grow-1">
                                                    <div className="fw-medium text-truncate">
                                                        {category.label}
                                                    </div>
                                                </div>
                                            </div>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            <div className="d-flex gap-2">
                                {hasEditPermission && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border-0"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={() => openModal()}
                                    >
                                        <FiPlus size={14} />
                                        <span className="d-none d-sm-inline">Add Product</span>
                                    </Button>
                                )}

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={fetchData}
                                    disabled={pending}
                                >
                                    <FiRefreshCw size={14} className={pending ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {activeFilter && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <span className="d-none d-sm-inline">Clear Filter</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-2">
                <Card.Body className="p-0">
                    <div
                        className="p-3 border-bottom bg-gray-50 d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setStatsCollapsed(!statsCollapsed)}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: '#d3ebffff'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FiTrendingUp size={14} className="text-primary me-2" />
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                                Product Statistics
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <FiChevronDown
                                size={14}
                                className={`text-muted transition-transform ${statsCollapsed ? '' : 'rotate-180'}`}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transform: statsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                            />
                        </div>
                    </div>

                    {!statsCollapsed && (
                        <div className="pt-4">
                            <Row className="g-3">
                                <Col md={3}>
                                    <StatCard
                                        title="Total Products"
                                        value={productStats.totalProducts}
                                        icon={<FiPackage />}
                                        color="#374151"
                                        filterType="all"
                                        isActive={!activeFilter}
                                        onFilter={() => clearFilters()}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Stock Value"
                                        value={formatPrice(productStats.totalStockValue)}
                                        subtitle={`${productStats.totalQuantity} total units`}
                                        icon={<FiDollarSign />}
                                        color="#8b5cf6"
                                        filterType="all"
                                        isActive={!activeFilter}
                                        onFilter={() => clearFilters()}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Active Products"
                                        value={productStats.activeProducts}
                                        icon={<FiCheckCircle />}
                                        color="#16a34a"
                                        filterType="status"
                                        filterValue="Active"
                                        isActive={activeFilter?.type === 'status' && activeFilter?.value === "Active"}
                                        onFilter={() => handleStatusFilter("Active")}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Inactive Products"
                                        value={productStats.inactiveProducts}
                                        icon={<FiAlertCircle />}
                                        color="#6b7280"
                                        filterType="status"
                                        filterValue="Inactive"
                                        isActive={activeFilter?.type === 'status' && activeFilter?.value === "Inactive"}
                                        onFilter={() => handleStatusFilter("Inactive")}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search products by name, SKU, category, or description"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2">
                                    <Badge bg="success" className="px-2 py-1">
                                        {filteredProducts.filter(p => p.status === "Active").length} Active
                                    </Badge>
                                    <Badge bg="secondary" className="px-2 py-1">
                                        {filteredProducts.filter(p => p.status === "Inactive").length} Inactive
                                    </Badge>
                                    {activeFilter && (
                                        <Badge bg="primary" className="px-2 py-1">
                                            Filter: {activeFilter.type === 'status'
                                                ? `Status: ${activeFilter.value}`
                                                : activeFilter.type === 'category'
                                                    ? `Category: ${activeFilter.value || 'All'}`
                                                    : activeFilter.type === 'stock'
                                                        ? (activeFilter.value === 'critical' ? 'Critical Stock' :
                                                            activeFilter.value === 'low' ? 'Low Stock' :
                                                                'Out of Stock')
                                                        : activeFilter.type}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        {pending ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading products...</span>
                            </div>
                        ) : filteredProducts.length > 0 ? (
                            <DataTable
                                keyField="product_id"
                                columns={columns}
                                data={filteredProducts}
                                progressPending={pending}
                                pagination
                                responsive
                                highlightOnHover
                                striped
                                dense
                                defaultSortFieldId="product_id"
                                defaultSortAsc={false}
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 100px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            color: "#374151"
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="text-center my-5">
                                <p className="text-muted">No products found</p>
                                {hasEditPermission && (
                                    <Button variant="outline-primary" onClick={() => openModal()}>
                                        <FiPlus className="me-1" /> Add Product
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
                scrollable
                className="product-modal"
                backdrop="static"
            >
                <Modal.Header className="bg-white border-0 p-4 shadow-sm position-sticky top-0 z-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h5 className="mb-0 fw-bold text-dark">{editingId ? "Edit Product" : "Add Product"}</h5>
                        <Button
                            variant=""
                            size="sm"
                            onClick={() => setShowModal(false)}
                            className="p-1 border-0 bg-transparent rounded-circle"
                        >
                            <FiX size={18} className="text-muted" />
                        </Button>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <Form>
                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-primary ps-3">Basic Information</h6>
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1 text-danger">SKU *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="sku"
                                    value={formData.sku}
                                    onChange={handleChange}
                                    size="sm"
                                    className="rounded-2"
                                    required
                                />
                            </div>
                            <div className="col-lg-6">
                                <Form.Label className="fw-semibold small mb-1 text-danger">Product Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="product_name"
                                    value={formData.product_name}
                                    onChange={handleChange}
                                    size="sm"
                                    className="rounded-2"
                                    required
                                />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-lg-4" style={{ fontSize: "14px" }}>
                                <Form.Label className="fw-medium text-muted small mb-1">Category</Form.Label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find((c) => c.value === formData.category)}
                                    onChange={(opt) => setFormData((prev) => ({ ...prev, category: opt.value }))}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '38px',
                                            borderRadius: '8px',
                                            border: state.isFocused ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                            boxShadow: 'none',
                                            backgroundColor: 'white'
                                        }),
                                        menu: (base) => ({ ...base, borderRadius: '8px', marginTop: '2px' })
                                    }}
                                    placeholder="Select..."
                                />
                            </div>
                            <div className="col-lg-4">
                                <Form.Label className="fw-medium text-muted small mb-1">Color</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    size="sm"
                                    className="rounded-2"
                                />
                            </div>
                            <div className="col-lg-4" style={{ fontSize: "14px" }}>
                                <Form.Label className="fw-medium text-muted small mb-1">Status</Form.Label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find((opt) => opt.value === formData.status)}
                                    onChange={(opt) => setFormData((prev) => ({ ...prev, status: opt.value }))}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '30px',
                                            borderRadius: '8px',
                                            border: state.isFocused ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                            boxShadow: 'none',
                                            backgroundColor: 'white'
                                        }),
                                        menu: (base) => ({ ...base, borderRadius: '8px', marginTop: '2px' })
                                    }}
                                    placeholder="Select..."
                                />
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-12">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-info ps-3">Specifications</h6>
                            </div>
                            <div className="col-lg-4">
                                <Form.Label className="fw-medium text-muted small mb-1">Capacity</Form.Label>
                                <Form.Control type="text" name="capacity" value={formData.capacity} onChange={handleChange} size="sm" className="rounded-2" />
                            </div>
                            <div className="col-lg-4">
                                <Form.Label className="fw-medium text-muted small mb-1">Size & Weight</Form.Label>
                                <Form.Control type="text" name="size_and_weight" value={formData.size_and_weight} onChange={handleChange} size="sm" className="rounded-2" />
                            </div>
                            <div className="col-lg-4">
                                <Form.Label className="fw-medium text-muted small mb-1">Display</Form.Label>
                                <Form.Control type="text" name="display" value={formData.display} onChange={handleChange} size="sm" className="rounded-2" />
                            </div>
                        </div>

                        <div className="mb-4">
                            <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-gray ps-3">Description</h6>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="product_description"
                                value={formData.product_description}
                                onChange={handleChange}
                                className="rounded-2"
                                placeholder="Enter product description..."
                                style={{ resize: 'vertical', minHeight: '80px' }}
                            />
                        </div>

                        <div className="mb-4">
                            <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-success ps-3">Pricing</h6>
                            <div className="row g-3">
                                <div className="col-lg-3 col-md-6">
                                    <Form.Label className="fw-semibold small mb-1 text-danger">Price *</Form.Label>
                                    <Form.Control type="number" name="price" value={formData.price} onChange={handleChange} className="rounded-2" required />
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <Form.Label className="fw-semibold small mb-1 text-danger">Sale Price *</Form.Label>
                                    <Form.Control type="number" name="sales_price" value={formData.sales_price} onChange={handleChange} className="rounded-2" required />
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <Form.Label className="fw-medium text-muted small mb-1">Discount</Form.Label>
                                    <Form.Control type="number" name="discount_price" value={formData.discount_price} onChange={handleChange} className="rounded-2" />
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <Form.Label className="fw-medium text-muted small mb-1">Margin</Form.Label>
                                    <Form.Control type="number" name="margin_price" value={formData.margin_price} onChange={handleChange} className="rounded-2" />
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-lg-6">
                                <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-warning ps-3">Stock</h6>
                                <Form.Label className="fw-semibold small mb-1 text-danger">Quantity *</Form.Label>
                                <Form.Control type="number" name="quantity" value={formData.quantity} onChange={handleChange} className="rounded-2" required />
                            </div>
                        </div>

                        <div>
                            <h6 className="fw-semibold text-muted mb-3 pb-2 border-start border-3 border-secondary ps-3">Product Image</h6>
                            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                                <Tab eventKey="upload" title={<><FiUpload className="me-1" size={14} />Upload</>}>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        className="rounded-2"
                                    />
                                </Tab>
                                <Tab eventKey="url" title={<><FiLink className="me-1" size={14} />URL</>}>
                                    <Form.Control
                                        type="url"
                                        placeholder="https://example.com/image.jpg"
                                        value={formData.product_img}
                                        onChange={handleImageUrlChange}
                                        className="rounded-2"
                                    />
                                </Tab>
                            </Tabs>
                            {imagePreview && (
                                <div className="text-center mt-3">
                                    <div className="bg-light rounded-2 p-2 d-inline-block">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="rounded"
                                            style={{ width: '140px', height: '140px', objectFit: 'cover' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                </Modal.Body>

                <Modal.Footer className="bg-white border-0 p-4 shadow-sm position-sticky bottom-0 z-3">
                    <div className="d-flex justify-content-end w-100 gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)} className="px-4 rounded-2">
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="px-4 rounded-2"
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {editingId ? "Updating..." : "Save Product"}
                                </>
                            ) : (
                                editingId ? "Update Product" : "Save Product"
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                size="md"
                backdrop="static"
            >
                <Modal.Body className="p-4 text-center rounded-3 shadow">
                    <div className="mb-4 p-3 rounded-circle d-inline-block bg-danger bg-opacity-10 border border-danger border-opacity-25">
                        <FiAlertTriangle size={40} className="text-danger" />
                    </div>

                    <h5 className="fw-bold text-dark mb-3">Delete Product?</h5>

                    <div className="text-muted mb-4 px-3" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                        This action will permanently remove the product from your inventory.
                        <br className="d-none d-md-block" />
                        <small>This cannot be undone.</small>
                    </div>

                    <div className="d-flex justify-content-center justify-content-md-end gap-2 flex-column flex-sm-row">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-3 border-0 shadow-sm"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleDeleteConfirmed}
                            className="px-4 rounded-3 fw-semibold shadow-sm"
                        >
                            Delete Product
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Products;